import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";

import { getMasterBeacon, deleteMasterBeacon, statusMasterBeacon } from "../../../core/data/redux/masterBeaconSlice";
import AssignMasterBeacon from "./assignMasterBeacon";
import { useDispatch, useSelector } from "react-redux";
import DeleteModal from "../../../core/modals/deleteModal";
import Toasts from "../../../core/common/toast";
import ChangeStatusModal from "../../../core/modals/changeStatusModal";
import PermissionGuard from "../../permissionGuard";

interface MasterBeaconListProps {}

const MasterBeaconList = (props: MasterBeaconListProps) => {

  const tableRef = useRef<{ resetSelection: (data: any) => void }>(null);
  const [currentItem, setCurrentItem] = useState<any>(null);
  const dispatch = useDispatch();
  const masterBeaconState = useSelector((state: any) => state.masterBeaconState);
  const [dataList, setDataList] = React.useState<any[]>([]);
  
  const routes = all_routes;
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10
  });
  const [selectedRows, setSelectedRows] = useState<any[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const handleReset = () => {
    if (tableRef.current) {
      tableRef.current.resetSelection([]);
    }
  };

  React.useEffect(() => {
    dispatch(getMasterBeacon({ page: pagination.current, limit: pagination.pageSize }));
  }, [dispatch]);

  const handleDelete = async (response: string) => {
    if (response === 'YES' && currentItem) {
      await dispatch(deleteMasterBeacon({ id: currentItem?.id }));
      await dispatch(getMasterBeacon({ page: pagination.current, limit: pagination.pageSize }));
    };
    setCurrentItem(null);
  }

  const handleChangeStatus = async (response: string) => {
    if (response === 'YES' && currentItem) {
      await dispatch(statusMasterBeacon({ ids: [currentItem.id], status: (currentItem.status === 'active') ? 'inactive' : 'active' }));
      await dispatch(getMasterBeacon({ page: pagination.current, limit: pagination.pageSize }));
    }
    setCurrentItem(null);
  }

  React.useEffect(() => {
    if (masterBeaconState.loading === "fulfilled") {
      if (masterBeaconState.action === "list") {
        const result = masterBeaconState?.data || null;
        
        if (result && result.data && Array.isArray(result.data) && result.data.length > 0) {
          let masterBeacons = result.data.map((item: any) => ({
            id: item.id,
            uuid: item.uuid,
            major: item.major,
            minor: item.minor,
            beaconName: item.beaconName || 'N/A',
            locationDescription: item.locationDescription || 'N/A',
            status: item.status,
            key: item.id
          }));
          setDataList(masterBeacons);
          setPagination({
            ...pagination,
            total: result.total || masterBeacons.length,
            current: result.page || 1,
            pageSize: result.limit || 10
          });
        } else {
          setDataList([]);
        }
      }
    }
  }, [masterBeaconState.loading, masterBeaconState.action, masterBeaconState.data]);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {(pagination.current) ? (index + pagination.current * pagination.pageSize - pagination.pageSize + 1) : (index + 1)}
          </div>
        </>
      ),
      sorter: (a: any, b: any) => a.id - b.id,
    },
    {
      title: "UUID",
      dataIndex: "uuid",
      sorter: (a: any, b: any) => a.uuid.localeCompare(b.uuid),
    },
    {
      title: "Major",
      dataIndex: "major",
      sorter: (a: any, b: any) => a.major - b.major,
    },
    {
      title: "Minor",
      dataIndex: "minor",
      sorter: (a: any, b: any) => a.minor - b.minor,
    },
    {
      title: "Beacon Name",
      dataIndex: "beaconName",
      sorter: (a: any, b: any) => a.beaconName.localeCompare(b.beaconName),
    },
    {
      title: "Location Description",
      dataIndex: "locationDescription",
      sorter: (a: any, b: any) => a.locationDescription.localeCompare(b.locationDescription),
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (text: string, record: any) => (
        <Link to="#" data-bs-toggle="modal" data-inert={true} data-bs-target="#change_status_modal" onClick={() => setCurrentItem(record)}>
          <span className={`badge ${(record.status === 'active') ? 'badge-success' : 'badge-danger'} d-inline-flex align-items-center badge-xs`}>
            <i className="ti ti-point-filled me-1" />
            {record.status === 'active' ? 'Active' : 'Inactive'}
          </span>
        </Link>
      ),
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: any, index: number) => (
        <div className="action-icon d-inline-flex">
          <PermissionGuard route="master-beacon/edit">
            <Link
              to={routes.masterBeacon.edit.replace(':id', record.id)}
              className="me-2"
            >
              <i className="ti ti-edit" />
            </Link>
          </PermissionGuard>
          <PermissionGuard route="master-beacon/delete">
            <Link to="#" data-bs-toggle="modal" data-inert={true} data-bs-target="#delete_modal" onClick={() => setCurrentItem(record)}>
              <i className="ti ti-trash" />
            </Link>
          </PermissionGuard>
          <PermissionGuard route="master-beacon/assign">
            <button
              className="btn btn-info btn-sm"
              data-bs-toggle="modal"
              data-inert={true}
              data-bs-target="#assign_master_beacon_modal"
              onClick={() => setCurrentItem(record)}
            >
              Assign
            </button>
          </PermissionGuard>
        </div>
      ),
    }
  ];

  const handlePagination = (pagination: any) => {
    setPagination({
      current: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total || 10
    });
    dispatch(getMasterBeacon({ page: pagination.current, limit: pagination.pageSize }));
  };

  const handleOnRowSelection = (selectedRowKeys: any) => {
    setSelectedRows(selectedRowKeys);
    setStatus(null);
  };

  const handleStatusChange = async () => {
    await dispatch(statusMasterBeacon({ ids: selectedRows, status: (status === 'Active') ? 'active' : 'inactive' }));
    setSelectedRows(null);
    setStatus(null);
    handleReset();
    dispatch(getMasterBeacon({ page: pagination.current, limit: pagination.pageSize }));
  }

  return (<div>
    <>
      <Toasts show={masterBeaconState.loading === "rejected"} message={masterBeaconState.error} title={'Failed'} type={'danger'} />

      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Master Beacons</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Master Beacons</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Beacon List
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <PermissionGuard route="master-beacon/add">
                  <Link
                    to={routes.masterBeacon.add}
                    className="btn btn-primary d-flex align-items-center"
                  >
                    <i className="ti ti-square-rounded-plus me-2" />
                    Add Beacon
                  </Link>
                </PermissionGuard>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-4 mt-3">Beacon List</h4>
              <div className="d-flex align-items-center flex-wrap">
                {selectedRows && selectedRows.length > 0 &&
                  <PermissionGuard route="master-beacon/change-status">
                    <div className="dropdown me-3 mb-0">
                      <Link
                        to="#"
                        className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                        data-bs-toggle="dropdown"
                      >
                        {status || 'Select Status'}
                      </Link>
                      <ul className="dropdown-menu  dropdown-menu-end p-3">
                        <li>
                          <Link
                            to="#"
                            className={`dropdown-item rounded-1 ${status === 'Active' ? 'active' : ''}`}
                            onClick={() => {
                              setStatus('Active');
                            }}
                          >
                            Active
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className={`dropdown-item rounded-1 ${status === 'Inactive' ? 'active' : ''}`}
                            onClick={() => {
                              setStatus('Inactive');
                            }}
                          >
                            Inactive
                          </Link>
                        </li>
                      </ul>
                    </div>
                    {status &&
                      <div className="dropdown me-3 mb-0">
                        <button
                          className="btn btn-primary w-100"
                          type="button" onClick={handleStatusChange}>Change Status</button>
                      </div>}
                  </PermissionGuard>
                }
              </div>
            </div>

            <div className="card-body p-0 py-3">
              <Table
                columns={columns}
                dataSource={dataList}
                Selection={true}
                paginationConfig={pagination}
                handlePagination={handlePagination}
                loading={masterBeaconState.loading === "pending"}
                onRowSelection={handleOnRowSelection}
                ref={tableRef}
              />
            </div>
          </div>

          <div className="row align-items-center">
            <div className="col-md-12">
              <div className="datatable-paginate mt-4" />
            </div>
          </div>
        </div>
      </div>
    </>
    <DeleteModal handleDelete={handleDelete} />
    <ChangeStatusModal handleChangeStatus={handleChangeStatus} />

    {/* Assign Master Beacon Modal */}
    <div
      className="modal fade"
      id="assign_master_beacon_modal"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="assignMasterBeaconModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <div className="d-flex align-items-center">
              <h4 className="modal-title me-2">Assign Beacon to Employees</h4>
              <span>
                Beacon: {currentItem?.beaconName || `ID: ${currentItem?.id || "0"}`}
              </span>
            </div>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <p className="text-muted">
                Select employee beacon assignments from the list below to assign this beacon. Assignments currently for this beacon will be pre-selected.
              </p>
            </div>
            <AssignMasterBeacon beacon={currentItem} />
          </div>
        </div>
      </div>
    </div>
  </div>
  );
}

export default MasterBeaconList; 